import { useMemo } from 'react';
import {
  STATUS_OPTIONS,
  PRESCRIPTION_OPTIONS,
  GST_RATE_OPTIONS,
  PACK_QUANTITY_OPTIONS,
  MANUFACTURER_OPTIONS,
  PACK_SIZE_OPTIONS,
} from '../../constants';

export const useMedicinesFormFields = () => {
  return useMemo(
    () => [
      // Row 1: Name | HSN
      {
        name: 'name',
        label: 'Medicine Name',
        type: 'text' as const,
        required: true,
        placeholder: 'Enter medicine name',
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 50,
        },
        tooltip: 'Enter the complete medicine name',
      },
      {
        name: 'hsn',
        label: 'HSN Code',
        type: 'text' as const,
        required: false,
        placeholder: 'Enter HSN code',
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 30,
        },
        tooltip: 'Harmonized System of Nomenclature code for taxation',
      },

      // Row 2: Manufacturer | Search Term
      {
        name: 'manufacturer',
        label: 'Manufacturer',
        type: 'text' as const,
        required: false,
        placeholder: 'Enter manufacturer name',
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 30,
        },
        tooltip: 'Select manufacturer from list or enter new one',
      },
      {
        name: 'search_term',
        label: 'Search Terms',
        type: 'searchTermTag' as const,
        required: false,
        placeholder: 'Add search terms...',
        layout: { span: 12 },
        maxTags: 10,
        props: {
          size: 'large',
        },
        tooltip: 'Add search terms to help filter medicines',
      },

      // Row 3: Pack Size | Pack Quantity
      {
        name: 'pack_size',
        label: 'Pack Size',
        type: 'text' as const,
        required: false,
        placeholder: 'Enter pack size',
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 30,
        },
        tooltip: 'Select pack size from list or enter custom size',
      },
      {
        name: 'pack_quantity',
        label: 'Pack Quantity',
        type: 'select' as const,
        required: false,
        placeholder: 'Select pack quantity',
        layout: { span: 12 },
        options: PACK_QUANTITY_OPTIONS,
        defaultValue: 1,
        props: {
          size: 'large',
          showSearch: true,
        },
        tooltip: 'Number of units in one pack',
      },

      // Row 4: MRP | GST Rate
      {
        name: 'mrp',
        label: 'MRP (₹)',
        type: 'number' as const,
        required: true,
        placeholder: 'Enter MRP',
        layout: { span: 12 },
        props: {
          size: 'large',
          min: 0,
          step: 0.01,
          precision: 2,
          addonBefore: '₹',
        },
        tooltip: 'Maximum Retail Price in Indian Rupees',
      },

      // Row 4: GST Rate | Status & Prescription Toggles
      {
        name: 'gst_rate',
        label: 'GST Rate (%)',
        type: 'select' as const,
        required: false,
        placeholder: 'Select GST rate',
        layout: { span: 12 },
        options: GST_RATE_OPTIONS,
        defaultValue: 12,
        props: {
          size: 'large',
        },
        tooltip: 'Goods and Services Tax rate applicable',
      },
      {
        name: 'is_active',
        label: 'Status',
        type: 'switch' as const,
        required: false,
        layout: { span: 8 },
        defaultValue: true,
        props: {
          size: 'default',
          checkedChildren: 'Active',
          unCheckedChildren: 'Inactive',
        },
        tooltip: 'Enable/disable medicine availability',
      },
      {
        name: 'requires_prescription',
        label: 'Requires Prescription',
        type: 'switch' as const,
        required: false,
        layout: { span: 8 },
        defaultValue: false,
        props: {
          size: 'default',
          checkedChildren: 'Required',
          unCheckedChildren: 'Not Required',
        },
        tooltip: 'Whether prescription is mandatory for this medicine',
      },

      // Row 5: Description (Full Width)
      {
        name: 'description',
        label: 'Description',
        type: 'textarea' as const,
        required: false,
        placeholder: 'Enter medicine description, usage instructions, etc.',
        layout: { span: 24 },
        props: {
          size: 'large',
          rows: 3,
          maxLength: 200,
          showCount: true,
        },
        tooltip: 'Additional information about the medicine',
      },
    ],
    []
  );
};